import { createTheme } from '@mui/material/styles';

/**
 * The SEP design values, transplanted from the existing SEP frontend (doc 09).
 *
 * Values only — no code is ported from that project. Sources:
 *   #689F38 / #8BC34A / #DCEDC8 / #0e72b5 — `client_styles/Global.css` and
 *   `components/mui/themes.js`. The house style is flat and squared-off:
 *   outlined surfaces, hairline dividers, sharp table corners, gently rounded
 *   cards, and no drop shadows on cards.
 */
export const SEP_GREEN = '#689F38';
export const SEP_GREEN_LIGHT = '#8BC34A';
export const SEP_GREEN_PALE = '#DCEDC8';
export const SEP_BLUE = '#0e72b5';
export const SEP_RED = '#d80000';
export const SEP_AMBER = '#ffaa00';
export const SEP_DIVIDER = '#BDBDBD';

export const APP_BAR_HEIGHT = 48;
export const DRAWER_WIDTH = 240;

export const theme = createTheme({
  palette: {
    primary: {
      main: SEP_GREEN,
      light: SEP_GREEN_LIGHT,
      dark: '#4d7a26',
      contrastText: '#FFFFFF',
    },
    secondary: { main: SEP_BLUE, contrastText: '#FFFFFF' },
    // #d80000 rather than the theme file's pure red: it is what users actually
    // see today, and pure red renders harshly next to the muted green.
    error: { main: SEP_RED },
    warning: { main: SEP_AMBER },
    success: { main: SEP_GREEN },
    divider: SEP_DIVIDER,
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    background: { default: '#F5F5F5', paper: '#FFFFFF' },
  },
  shape: { borderRadius: 4 },
  typography: {
    fontFamily: "'Open Sans', Roboto, Helvetica, Arial, sans-serif",
    h1: { fontSize: '1.75rem', fontWeight: 600 },
    h2: { fontSize: '1.375rem', fontWeight: 600 },
    h3: { fontSize: '1.125rem', fontWeight: 600 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    // Set once here rather than per button: German UI labels in ALL CAPS are
    // hard to read, and the SEP frontend ends up repeating this on every button.
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
  },
  components: {
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: { borderRadius: 8, borderColor: SEP_DIVIDER } },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } },
    },
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiChip: { defaultProps: { size: 'small' } },
    MuiTableContainer: { styleOverrides: { root: { borderRadius: 0 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: SEP_DIVIDER },
        head: { fontWeight: 600, backgroundColor: '#FAFAFA' },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'primary' },
      styleOverrides: { root: { borderBottom: `1px solid ${SEP_DIVIDER}` } },
    },
    MuiTooltip: { defaultProps: { arrow: true } },
  },
});
